<template>
  <el-drawer
    title="约量新增"
    :visible.sync="show"
    size="960px"
    append-to-body
    :wrapper-closable="false"
    custom-class="reserve-add-drawer"
  >
    <div class="drawer-body">
      <div class="drawer-left">
        <div class="section-title">商品信息</div>

        <el-form label-width="80px" size="small">
          <el-form-item
            label="选择商品"
            :class="{ 'is-product-error': productError }"
          >
            <el-select
              v-model="productId"
              filterable
              remote
              clearable
              reserve-keyword
              :remote-method="searchProduct"
              :loading="productLoading"
              placeholder="请选择"
              style="width: 100%;"
              @change="handleProductChange"
              @focus="handleProductFocus"
            >
              <el-option
                v-for="item in productOptions"
                :key="item.id"
                :label="getProductLabel(item)"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-form>

        <div v-if="selectedProduct" class="product-preview">
          <div class="product-image">
            <el-image
              v-if="selectedProduct.productImage"
              class="image"
              :src="selectedProduct.productImage"
              fit="cover"
            />
            <img v-else src="../../../../../assets/images/default.png" class="image" />
          </div>
          <div class="product-title">{{ productName }}</div>
          <div v-if="selectedProduct.expectedCommission" class="expected-commission">
            预期佣金：{{ selectedProduct.expectedCommission }}
          </div>
        </div>
      </div>

      <div class="drawer-right">
        <div class="form-section-title">约量信息提交</div>
        <el-form
          ref="formRef"
          :model="form"
          :rules="rules"
          :validate-on-rule-change="false"
          label-width="120px"
          size="small"
        >
          <el-form-item
            label="选择渠道"
            prop="channelAccountId"
          >
            <el-popover
              v-model="channelPopoverVisible"
              placement="bottom-start"
              width="520"
              trigger="click"
              append-to-body
              popper-class="channel-select-popover"
              @show="handleChannelPopoverShow"
            >
              <div class="channel-select-panel">
                <el-input
                  v-model="channelSearchKeyword"
                  size="small"
                  clearable
                  placeholder="请输入渠道名称或手机号"
                  @keyup.enter.native="searchChannelList"
                  @clear="handleChannelSearchClear"
                >
                  <el-button slot="append" icon="el-icon-search" @click="searchChannelList" />
                </el-input>
                <el-table
                  :data="channelList"
                  v-loading="channelLoading"
                  size="small"
                  max-height="280"
                  class="channel-select-table"
                  @row-click="handleChannelRowClick"
                >
                  <el-table-column width="48" align="center" label="">
                    <template slot-scope="{ row }">
                      <el-radio
                        v-model="selectedChannelId"
                        :label="row.accountId"
                        class="channel-table-radio"
                        @change="handleChannelSelect(row)"
                      />
                    </template>
                  </el-table-column>
                  <el-table-column prop="accountId" label="id" min-width="120" show-overflow-tooltip />
                  <el-table-column prop="nickName" label="渠道名称" min-width="120" show-overflow-tooltip />
                  <el-table-column prop="mobilePhone" label="手机号" min-width="120" show-overflow-tooltip />
                </el-table>
                <div class="channel-select-pagination">
                  <el-pagination
                    small
                    background
                    layout="total, prev, pager, next"
                    :total="channelPageInfo.total"
                    :page-size="channelPageInfo.size"
                    :current-page="channelPageInfo.current"
                    @current-change="handleChannelPageChange"
                  />
                </div>
              </div>
              <el-input
                slot="reference"
                readonly
                :value="channelDisplayText"
                placeholder="请选择"
                class="channel-select-input"
                @mousedown.native.prevent
                @focus.native="$event.target.blur()"
              >
                <template slot="suffix">
                  <i
                    v-if="selectedChannel"
                    class="el-input__icon el-icon-circle-close channel-input-clear"
                    @click.stop="clearChannel"
                  />
                  <i
                    class="el-input__icon el-icon-arrow-down channel-input-arrow"
                    :class="{ 'is-reverse': channelPopoverVisible }"
                  />
                </template>
              </el-input>
            </el-popover>
          </el-form-item>

          <el-form-item label="下单路径" prop="salesPathCode">
            <el-select v-model="form.salesPathCode" placeholder="请选择" style="width: 100%;">
              <el-option
                v-for="item in salesPathOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="所需库存" prop="reservationType">
            <el-radio-group v-model="form.reservationType">
              <el-radio :label="RESERVATION_TYPE.QUANTITY">按数量预约</el-radio>
              <el-radio :label="RESERVATION_TYPE.GMV">按GMV预约</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item
            :label="form.reservationType === RESERVATION_TYPE.GMV ? '下单金额' : '下单数量'"
            prop="reservationAmount"
          >
            <el-input
              v-model="form.reservationAmount"
              :placeholder="form.reservationType === RESERVATION_TYPE.GMV ? '请输入下单金额' : '请输入下单数量'"
            >
              <template slot="append">{{ form.reservationType === RESERVATION_TYPE.GMV ? '元' : '件' }}</template>
            </el-input>
          </el-form-item>

          <el-form-item label="佣金" prop="commissionType">
            <el-radio-group v-model="form.commissionType">
              <el-radio :label="COMMISSION_TYPE.RATE">佣金比例</el-radio>
              <el-radio :label="COMMISSION_TYPE.FIXED">固定佣金</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item
            :label="form.commissionType === COMMISSION_TYPE.RATE ? '佣金比例' : '佣金金额'"
            prop="commissionValue"
          >
            <el-input
              v-model="form.commissionValue"
              :placeholder="form.commissionType === COMMISSION_TYPE.RATE ? '请输入佣金比例' : '请输入佣金金额'"
            >
              <template slot="append">{{ form.commissionType === COMMISSION_TYPE.RATE ? '%' : '元/件' }}</template>
            </el-input>
          </el-form-item>

          <el-form-item label="预期下单时间" prop="expectedOrderTime">
            <el-date-picker
              v-model="form.expectedOrderTime"
              type="datetime"
              placeholder="请选择预期下单时间"
              style="width: 100%;"
              value-format="yyyy-MM-dd HH:mm:ss"
              :picker-options="expectedOrderPickerOptions"
              @change="handleExpectedOrderTimeChange"
            />
          </el-form-item>

          <el-form-item label="备注">
            <el-input
              v-model="form.remark"
              type="textarea"
              :rows="4"
              maxlength="260"
              show-word-limit
              placeholder="请输入备注信息"
            />
          </el-form-item>
        </el-form>
      </div>
    </div>

    <div class="drawer-footer">
      <el-button @click="show = false">返回</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确定</el-button>
    </div>
  </el-drawer>
</template>

<script>
import { channelPageList } from '@/service/apis/group'
import { createBizVolume, getQdVolumeProductList } from '@/service/apis/supVolume'
import {
  RESERVATION_TYPE,
  COMMISSION_TYPE,
  getProductFullName,
  buildBizCreatePayload,
  loadSalesPathOptions,
  getDefaultVolumeForm,
  getVolumeFormRules,
  getChannelAccountIdRules,
  getExpectedOrderPickerOptions,
  validateVolumeForm
} from '@/page/component/sections/xpk/sup/sup-volume-fields'

export default {
  name: 'ReserveRecordAddDrawer',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      RESERVATION_TYPE,
      COMMISSION_TYPE,
      productId: '',
      selectedProduct: null,
      productError: false,
      productOptions: [],
      productLoading: false,
      salesPathOptions: [],
      submitLoading: false,
      channelPopoverVisible: false,
      channelSearchKeyword: '',
      channelList: [],
      channelLoading: false,
      selectedChannelId: '',
      selectedChannel: null,
      channelPageInfo: {
        current: 1,
        size: 10,
        total: 0
      },
      form: {
        ...getDefaultVolumeForm(),
        channelAccountId: ''
      },
      rules: {
        ...getVolumeFormRules(),
        channelAccountId: getChannelAccountIdRules()
      }
    }
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    productName() {
      return getProductFullName(this.selectedProduct)
    },
    expectedOrderPickerOptions() {
      return getExpectedOrderPickerOptions(this.form.expectedOrderTime)
    },
    channelDisplayText() {
      if (!this.selectedChannel) return ''
      const phone = this.selectedChannel.mobilePhone
      return phone
        ? `${this.selectedChannel.nickName}（${phone}）`
        : this.selectedChannel.nickName
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.resetDrawer()
      }
    },
    'form.reservationType'() {
      this.form.reservationAmount = ''
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate(['reservationAmount'])
      })
    },
    'form.commissionType'() {
      this.form.commissionValue = ''
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate(['commissionValue'])
      })
    }
  },
  methods: {
    handleExpectedOrderTimeChange() {
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.validateField('expectedOrderTime')
      })
    },
    resetDrawer() {
      this.productId = ''
      this.selectedProduct = null
      this.productError = false
      this.productOptions = []
      this.channelPopoverVisible = false
      this.channelSearchKeyword = ''
      this.channelList = []
      this.selectedChannelId = ''
      this.selectedChannel = null
      this.channelPageInfo.current = 1
      this.channelPageInfo.total = 0
      this.form = {
        ...getDefaultVolumeForm(),
        channelAccountId: ''
      }
      this.getProductList()
      this.loadSalesPathOptions()
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },
    loadSalesPathOptions() {
      return loadSalesPathOptions(this).then(list => {
        this.salesPathOptions = list
      })
    },
    getProductLabel(product) {
      return getProductFullName(product)
    },
    getProductList(queryString = '') {
      const opt = {}
      if (queryString) {
        opt.keyword = queryString
      }
      this.productLoading = true
      getQdVolumeProductList.call(this, opt).then(res => {
        this.productOptions = res || []
      }).finally(() => {
        this.productLoading = false
      })
    },
    searchProduct(query) {
      this.getProductList(query)
    },
    handleProductFocus() {
      if (!this.productOptions.length) {
        this.getProductList()
      }
    },
    handleProductChange(id) {
      this.productError = false
      if (!id) {
        this.selectedProduct = null
        this.clearFormValidate()
        return
      }
      this.selectedProduct = this.productOptions.find(item => item.id === id) || null
      this.clearFormValidate()
    },
    clearFormValidate() {
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },
    validateProduct() {
      if (!this.selectedProduct) {
        this.productError = true
        this.$message.warning('请先选择商品')
        return false
      }
      return true
    },
    handleChannelPopoverShow() {
      if (!this.channelList.length) {
        this.getChannelList()
      }
    },
    searchChannelList() {
      this.channelPageInfo.current = 1
      this.getChannelList()
    },
    handleChannelSearchClear() {
      this.channelSearchKeyword = ''
      this.searchChannelList()
    },
    handleChannelPageChange(page) {
      this.channelPageInfo.current = page
      this.getChannelList()
    },
    getChannelList() {
      const params = {
        current: this.channelPageInfo.current,
        size: this.channelPageInfo.size
      }
      const queryString = (this.channelSearchKeyword || '').trim()
      if (queryString) {
        params.queryString = queryString
      }
      this.channelLoading = true
      return channelPageList.call(this, params).then(res => {
        this.channelList = res.records || []
        this.channelPageInfo.total = res.total || 0
      }).finally(() => {
        this.channelLoading = false
      })
    },
    handleChannelRowClick(row) {
      this.handleChannelSelect(row)
    },
    handleChannelSelect(row) {
      if (!row) return
      this.selectedChannelId = row.accountId
      this.selectedChannel = row
      this.form.channelAccountId = row.accountId
      this.channelPopoverVisible = false
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate(['channelAccountId'])
      })
    },
    clearChannel() {
      this.selectedChannelId = ''
      this.selectedChannel = null
      this.form.channelAccountId = ''
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.validateField('channelAccountId')
      })
    },
    async handleSubmit() {
      if (!this.validateProduct()) return
      const valid = await validateVolumeForm(this.$refs.formRef, this)
      if (!valid) return

      const payload = buildBizCreatePayload({
        product: this.selectedProduct,
        form: this.form,
        salesPathOptions: this.salesPathOptions,
        channel: this.selectedChannel
      })

      this.submitLoading = true
      createBizVolume.call(this, payload).then(() => {
        this.$message.success('提交成功')
        this.$emit('success')
        this.show = false
      }).finally(() => {
        this.submitLoading = false
      })
    }
  }
}
</script>

<style scoped>
.drawer-body {
  display: flex;
  height: calc(100vh - 140px);
  padding: 0 20px;
  overflow: hidden;
}

.drawer-left {
  width: 320px;
  flex-shrink: 0;
  border-right: 1px solid #eee;
  padding-right: 20px;
  overflow-y: auto;
}

.section-title,
.form-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin: 16px 0;
}

.product-preview {
  flex-shrink: 0;
  margin-bottom: 12px;
}

.product-image {
  position: relative;
  width: 100%;
  padding-top: 100%;
  background-color: #f5f5f5;
  border-radius: 2px;
  overflow: hidden;
}

.product-image .image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-image .image ::v-deep .el-image__inner {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-title {
  margin-top: 12px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #333;
  text-align: center;
}

.expected-commission {
  margin-top: 8px;
  font-size: 13px;
  line-height: 20px;
  color: #666;
  text-align: center;
}

.drawer-right {
  flex: 1;
  padding-left: 24px;
  overflow-y: auto;
}

.drawer-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: auto;
  padding: 12px 24px;
  border-top: 1px solid #eee;
  background-color: #fff;
}

.drawer-footer ::v-deep .el-button {
  height: 32px;
  padding: 0 15px;
  line-height: 32px;
}

.drawer-footer .el-button + .el-button {
  margin-left: 12px;
}

.is-product-error ::v-deep .el-input__inner {
  border-color: #f5222d;
}

.channel-select-input {
  width: 100%;
  cursor: pointer;
}

.channel-select-input ::v-deep .el-input__inner {
  cursor: pointer;
}

.channel-input-arrow {
  transition: transform 0.3s;
}

.channel-input-arrow.is-reverse {
  transform: rotate(180deg);
}

.channel-input-clear {
  cursor: pointer;
}

.channel-input-clear:hover {
  color: #909399;
}

.channel-select-panel {
  padding: 4px 0 0;
}

.channel-select-table {
  margin-top: 10px;
}

.channel-select-table ::v-deep .channel-table-radio {
  margin-right: 0;
}

.channel-select-table ::v-deep .channel-table-radio .el-radio__label {
  display: none;
  padding-left: 0;
}

.channel-select-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
</style>

<style>
.reserve-add-drawer .el-drawer__body {
  position: relative;
  padding-bottom: 60px;
}

.channel-select-popover {
  padding: 12px;
}
</style>
